import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { IoChevronForward } from 'react-icons/io5';
import { FaHeadset, FaQuestionCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useScrollToTop from '../hooks/useScrollToTop';
import useInventoryStore from '../stores/inventoryStore';
import Select from 'react-select';
import { getTickets, createTicket } from '../services/support';

const SupportPage = () => {
	useScrollToTop();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [tickets, setTickets] = useState([]);
	const [loading, setLoading] = useState(true);
	const [expandedFaq, setExpandedFaq] = useState(null);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [newTicketTitle, setNewTicketTitle] = useState('');
	const [newTicketMessage, setNewTicketMessage] = useState('');
	const [submitting, setSubmitting] = useState(false);

	const themes = [
		{ value: 'payment', label: t('support.topics.payment') },
		{ value: 'withdraw', label: t('support.topics.withdraw') },
		{ value: 'bug', label: t('support.topics.bug') },
		{ value: 'other', label: t('support.topics.other') }
	];

	const [selectedTheme, setSelectedTheme] = useState(themes[0].value);
	const items = useInventoryStore(state => state.items);
	const fetchItems = useInventoryStore(state => state.fetchItems);
	const [selectedItemId, setSelectedItemId] = useState('');

	// Тестовые данные для FAQ
	const faqData = [
		{
			id: 1,
			question: t('support.faq.1.question'),
			answer: t('support.faq.1.answer')
		},
		{
			id: 2,
			question: t('support.faq.2.question'),
			answer: t('support.faq.2.answer')
		},
		{
			id: 3,
			question: t('support.faq.3.question'),
			answer: t('support.faq.3.answer')
		},
		{
			id: 4,
			question: t('support.faq.4.question'),
			answer: t('support.faq.4.answer')
		},
		{
			id: 5,
			question: t('support.faq.5.question'),
			answer: t('support.faq.5.answer')
		}
	];

	useEffect(() => {
		setLoading(true);
		getTickets()
			.then(data => setTickets(data))
			.catch(() => toast.error('Ошибка загрузки тикетов'))
			.finally(() => setLoading(false));
	}, []);

	useEffect(() => {
		if (showCreateModal && selectedTheme === 'withdraw' && items.length === 0) {
			fetchItems();
		}
	}, [showCreateModal, selectedTheme, items.length, fetchItems]);

	useEffect(() => {
		if (window.Telegram && window.Telegram.WebApp) {
			window.Telegram.WebApp.BackButton.show();
			window.Telegram.WebApp.BackButton.onClick(() => navigate(-1));
		}
		return () => {
			if (window.Telegram && window.Telegram.WebApp) {
				window.Telegram.WebApp.BackButton.hide();
				window.Telegram.WebApp.BackButton.offClick();
			}
		};
	}, [navigate]);

	const handleCreateTicket = () => {
		setShowCreateModal(true);
	};

	const handleModalClose = () => {
		setShowCreateModal(false);
		setNewTicketTitle('');
		setNewTicketMessage('');
	};

	const handleModalSubmit = async () => {
		if (submitting) return;
		if (selectedTheme === 'withdraw') {
			if (!selectedItemId) {
				toast.error('Пожалуйста, выберите предмет');
				return;
			}
			if (!newTicketMessage.trim()) {
				toast.error('Пожалуйста, заполните сообщение');
				return;
			}
		} else {
			if (!newTicketMessage.trim()) {
				toast.error('Пожалуйста, заполните сообщение');
				return;
			}
		}
		setSubmitting(true);
		try {
			await createTicket({
				theme: selectedTheme,
				item_id: selectedTheme === 'withdraw' ? selectedItemId : undefined,
				title: newTicketTitle || undefined,
				message: newTicketMessage
			});
			toast.success('Тикет успешно создан!');
			setShowCreateModal(false);
			setNewTicketTitle('');
			setNewTicketMessage('');
			setSelectedItemId('');
			// Обновить список тикетов
			const data = await getTickets();
			setTickets(data);
		} catch (e) {
			toast.error('Ошибка при создании тикета');
		} finally {
			setSubmitting(false);
		}
	};

	const handleTicketClick = (ticketId) => {
		navigate(`/support/ticket/${ticketId}`);
	};

	const handleBack = () => {
		navigate(-1);
	};

	const toggleFaq = (id) => {
		setExpandedFaq(expandedFaq === id ? null : id);
	};

	const getStatusColor = (status) => {
		return status === 'open' ? '#34c759' : '#8e8e93';
	};

	const getStatusText = (status) => {
		return status === 'open' ? t('support.status.open') : t('support.status.closed');
	};

	const formatDate = (dateString) => {
		const date = new Date(dateString);
		return date.toLocaleDateString('ru-RU', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	const handleItemChange = (e) => {
		const itemId = e.target.value;
		setSelectedItemId(itemId);
		const item = items.find(i => i.id === itemId);
		if (item) {
			setNewTicketMessage(t('support.withdrawTemplate', { name: item.marketHashName, id: item.id }));
		}
	};

	return (
		<Container>
			<Header>
				<HeaderTitle>{t('profile.support')}</HeaderTitle>
				<CreateTicketButton onClick={handleCreateTicket}>
					{t('profile.supportSection.createTicket')}
				</CreateTicketButton>
			</Header>

			<Content>
				{/* Секция тикетов */}
				<Section>
					<SectionTitle>
						<FaHeadset size={20} />
						<span>{t('profile.supportSection.myTickets')}</span>
					</SectionTitle>

					{loading ? (
						<LoadingText>{t('profile.supportSection.loadingTickets')}</LoadingText>
					) : tickets.length > 0 ? (
						<TicketsList>
							{tickets.map((ticket) => (
								<TicketCard key={ticket.id} onClick={() => handleTicketClick(ticket.id)}>
									<TicketHeader>
										<TicketTitle>{ticket.title || t('support.topics.' + ticket.theme)}</TicketTitle>
										<TicketStatus color={getStatusColor(ticket.status)}>
											{getStatusText(ticket.status)}
										</TicketStatus>
									</TicketHeader>
									<TicketMessage>{ticket.lastMessage}</TicketMessage>
									<TicketDate>{formatDate(ticket.created_at)}</TicketDate>
								</TicketCard>
							))}
						</TicketsList>
					) : (
						<EmptyState>
							<EmptyIcon>📝</EmptyIcon>
							<EmptyTitle>{t('profile.supportSection.noTickets')}</EmptyTitle>
							<EmptyText>{t('profile.supportSection.noTicketsText')}</EmptyText>
						</EmptyState>
					)}
				</Section>

				{/* Секция FAQ */}
				<Section>
					<SectionTitle>
						<FaQuestionCircle size={20} />
						<span>{t('profile.supportSection.faq')}</span>
					</SectionTitle>

					<FaqList>
						{faqData.map((faq) => (
							<FaqItem key={faq.id}>
								<FaqQuestion
									onClick={() => toggleFaq(faq.id)}
									isExpanded={expandedFaq === faq.id}
								>
									<span>{faq.question}</span>
									<IoChevronForward
										size={16}
										style={{
											transform: expandedFaq === faq.id ? 'rotate(90deg)' : 'rotate(0deg)',
											transition: 'transform 0.2s ease'
										}}
									/>
								</FaqQuestion>
								<FaqAnswer isExpanded={expandedFaq === faq.id}>
									{faq.answer}
								</FaqAnswer>
							</FaqItem>
						))}
					</FaqList>
				</Section>
			</Content>

			{showCreateModal && (
				<ModalOverlay>
					<ModalContent>
						<ModalTitle>{t('profile.supportSection.createTicket')}</ModalTitle>
						<ModalLabel>{t('support.topic')}</ModalLabel>
						<Select
							options={themes}
							value={themes.find(option => option.value === selectedTheme)}
							onChange={option => {
								setSelectedTheme(option.value);
								setSelectedItemId('');
								if (option.value !== 'withdraw') setNewTicketMessage('');
							}}
							styles={{
								control: (base) => ({
									...base,
									background: '#111',
									borderColor: '#333',
									borderRadius: 12,
									minHeight: 44,
									color: '#fff',
									boxShadow: 'none',
								}),
								singleValue: (base) => ({ ...base, color: '#fff' }),
								menu: (base) => ({ ...base, background: '#18181b', color: '#fff', borderRadius: 12 }),
								option: (base, state) => ({
									...base,
									background: state.isSelected ? '#232325' : state.isFocused ? '#232325' : 'transparent',
									color: '#fff',
									borderRadius: 8,
									paddingLeft: 18,
									paddingRight: 18,
								}),
								input: (base) => ({ ...base, color: '#fff' }),
							}}
							placeholder={t('support.topic')}
							isSearchable={false}
						/>
						{selectedTheme === 'withdraw' && (
							<>
								<ModalLabel>{t('support.selectItem')}</ModalLabel>
								<Select
									options={items.map(item => ({ value: item.id, label: item.name || item.marketHashName || item.id, iconUrl: item.iconUrl }))}
									value={items.find(i => i.id === selectedItemId) ? { value: selectedItemId, label: items.find(i => i.id === selectedItemId).name || items.find(i => i.id === selectedItemId).marketHashName || selectedItemId, iconUrl: items.find(i => i.id === selectedItemId).iconUrl } : null}
									onChange={option => {
										setSelectedItemId(option ? option.value : '');
										const item = items.find(i => i.id === (option ? option.value : ''));
										if (item) setNewTicketMessage(t('support.withdrawTemplate', { name: item.marketHashName, id: item.id }));
									}}
									styles={{
										control: (base) => ({
											...base,
											background: '#111',
											borderColor: '#333',
											borderRadius: 12,
											minHeight: 44,
											color: '#fff',
											boxShadow: 'none',
										}),
										singleValue: (base) => ({ ...base, color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }),
										menu: (base) => ({ ...base, background: '#18181b', color: '#fff', borderRadius: 12 }),
										option: (base, state) => ({
											...base,
											background: state.isSelected ? '#232325' : state.isFocused ? '#232325' : 'transparent',
											color: '#fff',
											borderRadius: 8,
											paddingLeft: 18,
											paddingRight: 18,
											display: 'flex', alignItems: 'center', gap: 10
										}),
										input: (base) => ({ ...base, color: '#fff' }),
										placeholder: (base) => ({ ...base, color: '#888' }),
									}}
									placeholder={t('support.placeholder.select')}
									isSearchable={false}
									formatOptionLabel={(option) => (
										<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
											{option.iconUrl && <img src={option.iconUrl} alt="icon" style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 8, background: '#222', marginRight: 8 }} />}
											<span>{option.label}</span>
										</div>
									)}
								/>
							</>
						)}
						<ModalLabel>{t('support.message')}</ModalLabel>
						<ModalTextarea
							value={newTicketMessage}
							onChange={e => setNewTicketMessage(e.target.value)}
							placeholder={t('support.placeholder.message')}
							rows={4}
						/>
						<ModalActions>
							<ModalButton onClick={handleModalClose}>{t('support.cancel')}</ModalButton>
							<ModalButton primary onClick={handleModalSubmit} disabled={submitting}>{submitting ? t('common.loading') : t('support.send')}</ModalButton>
						</ModalActions>
					</ModalContent>
				</ModalOverlay>
			)}
		</Container>
	);
};

const Container = styled.div`
  background: ${props => props.theme.background};
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: transparent;
  border: none;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.text.primary};
  margin: 0;
`;

const CreateTicketButton = styled.button`
  background: ${props => props.theme.primary};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: ${props => props.theme.primaryHover || '#2563eb'};
  }
  &:active {
    opacity: 0.8;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 16px 32px 16px;
`;

const Section = styled.div`
  margin-bottom: 36px;
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.text.primary};
  margin: 0 0 18px 0;
`;

const LoadingText = styled.div`
  text-align: center;
  color: ${props => props.theme.text.secondary};
  padding: 20px;
`;

const TicketsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TicketCard = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 12px;
  padding: 16px;
  border: 1px solid ${props => props.theme.border};
  cursor: pointer;
`;

const TicketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const TicketTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${props => props.theme.text.primary};
  flex: 1;
  margin-right: 12px;
`;

const TicketStatus = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.color};
  background: ${props => props.color}20;
  padding: 4px 8px;
  border-radius: 6px;
  white-space: nowrap;
`;

const TicketMessage = styled.div`
  font-size: 14px;
  color: ${props => props.theme.text.secondary};
  margin-bottom: 8px;
  line-height: 1.4;
`;

const TicketDate = styled.div`
  font-size: 12px;
  color: ${props => props.theme.text.secondary};
  opacity: 0.7;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${props => props.theme.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: ${props => props.theme.text.primary};
  margin-bottom: 8px;
`;

const EmptyText = styled.div`
  font-size: 14px;
  line-height: 1.4;
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: ${props => props.theme.card.background};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
`;

const FaqItem = styled.div`
  border-bottom: 1px solid ${props => props.theme.border};
  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  cursor: pointer;
  color: ${props => props.theme.text.primary};
  font-weight: 500;
  background: ${props => props.theme.card.background};
  font-size: 16px;
  &:active {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const FaqAnswer = styled.div`
  padding: ${props => props.isExpanded ? '0 20px 16px 20px' : '0 20px'};
  max-height: ${props => props.isExpanded ? '500px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  color: ${props => props.theme.text.secondary};
  line-height: 1.5;
  background: ${props => props.theme.card.background};
  font-size: 15px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.card.background};
  padding: 32px 24px;
  border-radius: 16px;
  min-width: 320px;
  max-width: 90vw;
  box-shadow: 0 4px 24px rgba(0,0,0,0.15);
`;

const ModalTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 20px;
  color: ${props => props.theme.text.primary};
`;

const ModalLabel = styled.div`
  font-size: 14px;
  color: ${props => props.theme.text.secondary};
  margin-bottom: 4px;
`;

const ModalSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.border};
  margin-bottom: 12px;
  font-size: 15px;
  color: ${props => props.theme.text.primary};
  background: ${props => props.theme.background};
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.border};
  margin-bottom: 16px;
  font-size: 15px;
  color: ${props => props.theme.text.primary};
  background: ${props => props.theme.background};
  resize: vertical;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ModalButton = styled.button`
  padding: 8px 18px;
  border-radius: 8px;
  border: none;
  font-size: 15px;
  font-weight: 500;
  background: ${props => props.primary ? props.theme.primary : props.theme.card.background};
  color: ${props => props.primary ? 'white' : props.theme.text.primary};
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: ${props => props.primary
		? (props.theme.primaryHover || '#2563eb')
		: (props.theme.cardHover || '#232325')};
  }
  &:active {
    opacity: 0.85;
  }
`;

export default SupportPage; 